<template>
  <div class="hidden-orders-tab">
    <header class="page-header">
      <div class="header-left">
        <h1 class="page-title">👁️‍🗨️ Скрытые заказы</h1>
        <p class="page-subtitle">Управление скрытыми заказами и потолками</p>
      </div>
      <button @click="fetchHiddenOrders" class="btn-refresh" :disabled="loading">
        <span class="refresh-icon">🔄</span>
        Обновить
      </button>
    </header>

    <!-- STATS CARDS -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">{{ stats.totalHidden }}</div>
        <div class="stat-label">Всего скрыто</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.hiddenOrders }}</div>
        <div class="stat-label">Скрытых заказов</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.hiddenCeilings }}</div>
        <div class="stat-label">Скрытых потолков</div>
      </div>
    </div>

    <!-- LOADING STATE -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Загрузка скрытых заказов...</p>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <p class="error-title">Ошибка загрузки данных</p>
      <p class="error-message">{{ error }}</p>
      <button @click="fetchHiddenOrders" class="btn-primary">Повторить попытку</button>
    </div>

    <!-- HIDDEN ORDERS TABLE -->
    <div v-else-if="hiddenOrders.length > 0" class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Номер заказа</th>
            <th>Клиент</th>
            <th>Тип</th>
            <th>Дата скрытия</th>
            <th>Скрыл</th>
            <th>Причина</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in hiddenOrders" :key="item.id">
            <td>{{ index + 1 }}</td>
            <td>
              <span class="order-number">{{ item.zakaz_number || item.potolok_zakaz_number || 'N/A' }}</span>
            </td>
            <td>{{ item.client_name || 'Не указано' }}</td>
            <td>
              <span class="type-badge" :class="item.type">
                {{ item.type === 'zakaz' ? 'Заказ' : 'Потолок' }}
              </span>
            </td>
            <td>{{ formatDate(item.hidden_at) }}</td>
            <td>{{ item.hidden_by || 'Система' }}</td>
            <td>
              <span class="reason">{{ item.reason || '—' }}</span>
            </td>
            <td>
              <button
                v-if="hasPermission('unhide_orders')"
                @click="unhideItem(item)"
                class="btn-unhide"
                :disabled="unhiding === item.id"
              >
                <span v-if="unhiding === item.id">⏳</span>
                <span v-else>👁️</span>
                Восстановить
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- EMPTY STATE -->
    <div v-else class="empty-state">
      <div class="empty-icon">✨</div>
      <h2 class="empty-title">Нет скрытых заказов</h2>
      <p class="empty-text">Все заказы и потолки активны</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { usePermissions } from '@/composables/usePermissions'

const API_BASE = 'https://mpapi.premside.uz/api/hidden'

export default {
  name: 'HiddenOrdersTab',
  setup() {
    const { hasPermission } = usePermissions()
    const hiddenOrders = ref([])
    const loading = ref(true)
    const error = ref(null)
    const unhiding = ref(null)

    // Статистика
    const stats = computed(() => {
      const hiddenOrdersCount = hiddenOrders.value.filter(item => item.type === 'zakaz').length
      const hiddenCeilingsCount = hiddenOrders.value.filter(item => item.type === 'potolok').length

      return {
        totalHidden: hiddenOrders.value.length,
        hiddenOrders: hiddenOrdersCount,
        hiddenCeilings: hiddenCeilingsCount
      }
    })

    /**
     * Загрузка скрытых заказов
     */
    const fetchHiddenOrders = async () => {
      loading.value = true
      error.value = null

      try {
        // Загружаем и заказы, и потолки параллельно
        const [zakazResponse, potolokResponse] = await Promise.all([
          axios.get(`${API_BASE}/zakaz/hidden`).catch(() => ({ data: { data: [] } })),
          axios.get(`${API_BASE}/potolok/hidden`).catch(() => ({ data: { data: [] } }))
        ])

        // Обрабатываем заказы
        let zakazData = []
        if (zakazResponse.data.data) {
          zakazData = zakazResponse.data.data
        }

        // Обрабатываем потолки
        let potolokData = []
        if (potolokResponse.data.data) {
          potolokData = potolokResponse.data.data
        }

        // Маппим заказы к ожидаемой структуре
        const mappedZakaz = zakazData.map(item => ({
          id: item.id_hidden,
          zakaz_id: item.id_zakaz,
          zakaz_number: item.num_zakaz,
          client_name: item.client_name,
          type: 'zakaz',
          hidden_at: item.date_hidden,
          hidden_by: item.hidden_by,
          reason: item.reason,
          date_in: item.date_in
        }))

        // Маппим потолки к ожидаемой структуре
        const mappedPotolok = potolokData.map(item => ({
          id: item.id_hidden,
          potolok_id: item.id_potolok,
          zakaz_number: item.num_potolok || item.num_zakaz,
          client_name: item.client_name,
          type: 'potolok',
          hidden_at: item.date_hidden,
          hidden_by: item.hidden_by,
          reason: item.reason,
          date_in: item.date_in
        }))

        // Объединяем и сортируем по дате скрытия (новые первыми)
        hiddenOrders.value = [...mappedZakaz, ...mappedPotolok].sort((a, b) =>
          new Date(b.hidden_at) - new Date(a.hidden_at)
        )
      } catch (err) {
        console.error('Error fetching hidden orders:', err)
        error.value = err.response?.data?.message || err.message || 'Не удалось загрузить скрытые заказы'
      } finally {
        loading.value = false
      }
    }

    /**
     * Восстановление заказа/потолка
     */
    const unhideItem = async (item) => {
      unhiding.value = item.id

      try {
        const endpoint = item.type === 'zakaz'
          ? `${API_BASE}/zakaz/${item.zakaz_id}/unhide`
          : `${API_BASE}/potolok/${item.potolok_id}/unhide`

        await axios.post(endpoint)

        // Удаляем из списка скрытых
        hiddenOrders.value = hiddenOrders.value.filter(i => i.id !== item.id)

        // Показываем уведомление
        console.log(`${item.type === 'zakaz' ? 'Заказ' : 'Потолок'} успешно восстановлен`)
      } catch (err) {
        console.error('Error unhiding item:', err)
        alert(err.response?.data?.message || err.message || 'Не удалось восстановить')

        // Перезагружаем данные в случае ошибки
        await fetchHiddenOrders()
      } finally {
        unhiding.value = null
      }
    }

    /**
     * Форматирование даты с учетом часового пояса Алматы (+5)
     */
    const formatDate = (dateString) => {
      if (!dateString) return 'Не указано'

      // Парсим дату с сервера (UTC)
      const dateUTC = new Date(dateString)

      // Добавляем 5 часов для Алматы
      const dateAlmaty = new Date(dateUTC.getTime() + 5 * 60 * 60 * 1000)

      // Текущее время тоже в Алматы (+5)
      const nowAlmaty = new Date(Date.now() + 5 * 60 * 60 * 1000)

      const diff = nowAlmaty - dateAlmaty
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))

      if (hours < 1) {
        return 'только что'
      } else if (hours < 24) {
        return `${hours} ч. назад`
      } else if (days < 7) {
        return `${days} дн. назад`
      } else {
        return dateAlmaty.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      }
    }

    onMounted(() => {
      fetchHiddenOrders()
    })

    return {
      hiddenOrders,
      loading,
      error,
      unhiding,
      stats,
      fetchHiddenOrders,
      unhideItem,
      formatDate,
      hasPermission
    }
  }
}
</script>

<style scoped>
.hidden-orders-tab {
  padding: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  color: #6b7280;
  font-size: 1rem;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-icon {
  font-size: 1.2rem;
}

/* STATS GRID */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: #fff;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  text-align: center;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #6366f1;
  margin-bottom: 0.25rem;
}

.stat-label {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

/* LOADING */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 1.5rem;
  color: #6b7280;
  font-size: 1rem;
}

/* ERROR */
.error-container {
  text-align: center;
  padding: 4rem 2rem;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.error-message {
  color: #6b7280;
  margin-bottom: 2rem;
}

.btn-primary {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
}

/* TABLE */
.table-container {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
}

.data-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.data-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
}

.data-table tbody tr:hover {
  background: #f9fafb;
}

.data-table td {
  padding: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.order-number {
  font-weight: 600;
  color: #111827;
}

.type-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.type-badge.zakaz {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.potolok {
  background: #fef3c7;
  color: #92400e;
}

.reason {
  font-style: italic;
  color: #9ca3af;
}

.btn-unhide {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #10b981;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-unhide:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-unhide:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.empty-text {
  color: #6b7280;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 1rem;
  }

  .btn-refresh {
    width: 100%;
    justify-content: center;
  }

  .table-container {
    overflow-x: auto;
  }

  .data-table {
    min-width: 800px;
  }
}
</style>
